/**
 * Every route path in the console.
 *
 * Ported from the legacy `src/core/enum/route.enum.ts` as an `as const` object rather than an
 * `enum` (CLAUDE.md > TypeScript). Paths are the legacy ones **verbatim**, including the ones
 * that are inconsistent with the rest — `/mlsCustomer-list` and `/allSetting` are camelCase,
 * `/groups-list/Whitelist/:group_id` is capitalised mid-path. They stay as they are: staff have
 * these URLs bookmarked, and prettifying them would break links for no user-visible gain.
 *
 * Renaming a path is a product decision with a redirect attached, not a tidy-up.
 */
export const ROUTES = {
  // ── Chrome ────────────────────────────────────────────────────────────────
  home: '/',
  dashboard: '/dashboard',
  error: '/error',

  // ── Auth ──────────────────────────────────────────────────────────────────
  signIn: '/login',
  emailCheck: '/emailCheck',
  register: '/register',
  forgotPassword: '/forgot-password',
  signOut: '/logOut',

  // ── Profile ───────────────────────────────────────────────────────────────
  profile: '/profile',
  profileSetup: '/profileSetup',

  // ── Customers ─────────────────────────────────────────────────────────────
  customers: '/customer-list',
  mlsCustomers: '/mlsCustomer-list',
  customerAnalytics: '/customer-analytics',
  mlsRequests: '/alerts',
  apiKeys: '/api-mgmt',
  apiKeyConfig: '/api-mgmt/config',
  apiKeyUsageLog: '/api-mgmt/usage-log',

  // ── Sales & Marketing ─────────────────────────────────────────────────────
  packages: '/base-packages',
  packageDetail: '/base-packages/detail/:id',
  packageAdd: '/base-packages/add',
  packageAddWithId: '/base-packages/add/:id',
  packageEdit: '/base-packages/edit/:id',
  usagePlans: '/usage-plan',
  addUsagePlan: '/add-usage-plan',
  editUsagePlan: '/edit-usage-plan/:id',
  usagePlanResources: '/resource-list',
  resources: '/resources',
  addResource: '/resources/add',
  billingAdjustment: '/billing-adjustment',
  billingAdjustmentDetail: '/billing-adjustment-details/:id',
  billingAdjustmentAdd: '/billing-adjustment/add',
  subscriptions: '/subscription-list',
  subscriptionDetail: '/subscription-list/detail/:id',
  invoices: '/invoice-management-list',
  invoiceDetail: '/invoice-management-list/detail/:id',

  // ── MLS / Node ────────────────────────────────────────────────────────────
  mlsProviders: '/mls-list',
  mlsLogs: '/mls-list/logs',
  mlsPlatforms: '/platforms',
  mlsServerInvoice: '/mls-server-invoice',
  mlsPaymentForecast: '/mls-payment-forecast',
  mlsPaymentPlans: '/mls-payment-plans',
  mlsPaymentPlanSetInfo: '/mls-payment-plans/set-info/:id',

  // ── IP Monitor ────────────────────────────────────────────────────────────
  blockedIps: '/blocked-ip',
  whitelistIps: '/whitelist-ips',

  // ── Group ─────────────────────────────────────────────────────────────────
  groups: '/groups-list',
  groupJoinRequests: '/groups-list/join-requests/:group_id',
  groupMembers: '/groups-list/members/:group_id',
  /** Capitalised mid-path in the legacy; staff have the URL bookmarked. */
  groupWhitelist: '/groups-list/Whitelist/:group_id',
  inviteGroupMember: '/groups-list/invite-member/:id',
  addGroup: '/groups-list/add-group',
  /** Where a create hands over — a group that exists, opened on the BTT step. */
  continueGroup: '/groups-list/add-group/:id',
  editGroup: '/groups-list/edit-group/:id',
  groupDetail: '/groups-list/:id',
  groupPaymentPlans: '/group-payment-plans',
  groupPaymentPlanSetInfo: '/group-payment-plans/set-info/:groupId',

  // ── Admins Mgmt ───────────────────────────────────────────────────────────
  users: '/users',
  userDetail: '/users/detail/:id',
  staff: '/staff',
  /** Whitelists email addresses so those people may sign up — it does not create a record. */
  inviteStaff: '/staff/add',
  staffDetail: '/staff/:id',
  roles: '/roles',
  addRole: '/roles/add',
  editRole: '/roles/edit/:id',
  permissions: '/permissions',
  addPermission: '/permissions/add',
  btt: '/btt-list',
  addBtt: '/btt-list/add',
  /** Where a create hands over — a BTT that exists, opened on the permissions step. */
  continueBtt: '/btt-list/add/:id',
  editBtt: '/btt-list/edit/:id',
  bttDetail: '/btt-list/:id',
  accountDeletion: '/account-deletion',
  filterTemplates: '/limitation-templates',
  addFilterTemplate: '/limitation-templates/add',
  editFilterTemplate: '/limitation-templates/edit/:id',

  // ── Notification ──────────────────────────────────────────────────────────
  notifications: '/notification-list',
  addNotification: '/notification-list/add',
  editNotification: '/notification-list/edit/:id/:code',
  notificationContent: '/notification-list/content/:id/:code/:name',
  notificationSettings: '/notification-setting-list',
  /**
   * The same wizard, opened from the notification **settings** list, where `:setting` is the
   * setting row's own id. The legacy never read its value — its presence is what says the wizard
   * should open on the settings step and that Finish returns to the settings list.
   *
   * The legacy also routed `/notification-list/edit/:id/:code/:setting` to the same screen.
   * Nothing links there — every `:setting` link in the app is this one — so it is not carried.
   */
  editNotificationFromSettings: '/notification-setting-list/edit/:id/:code/:setting',
  notificationVariables: '/notification-variables',
  microserviceTemplates: '/notification-application-template',
  addMicroserviceTemplate: '/notification-application-template/add',
  editMicroserviceTemplate: '/notification-application-template/edit/:id',
  /** The Applications screen. The path says "templates"; that mismatch is the legacy's. */
  notificationApplications: '/notification-templates',
  addNotificationApplication: '/notification-templates/add',
  editNotificationApplication: '/notification-templates/edit/:id',

  // ── Multi language / Agreements / GDX ─────────────────────────────────────
  multiLanguage: '/multilang',
  editMultiLanguage: '/multilang/edit/:id',
  agreementTemplates: '/agreement-templates',
  addAgreementTemplate: '/agreement-templates/add',
  editAgreementTemplate: '/agreement-templates/edit/:id',
  signedAgreements: '/signed-agreements',
  gdxPartner: '/gdx-partner',
  addGdxPartner: '/gdx-partner/add',
  editGdxPartner: '/gdx-partner/edit/:id',
  gdxPartnerDetail: '/gdx-partner/detail/:id',
  syncGdxPartner: '/gdx-partner/sync/:id',

  // ── Settings ──────────────────────────────────────────────────────────────
  /**
   * **No screen is registered at this path, on purpose.**
   *
   * The legacy `/allSetting` renders a Card with the title "Settings" and an **empty**
   * `CardBody` — 20 lines, no fields, no calls. Nothing links to it either: outside the route
   * table and the route enum, `ERoute.AllSetting` appears nowhere in the legacy app, and the
   * sidebar config has no entry for it. So it is an unreachable page that renders nothing.
   *
   * Kept as a constant because this is where someone asked to "build the settings screen" would
   * look, and the answer they need is that there was never anything here to port. See
   * `docs/07-decisions.md` D38.
   */
  settings: '/allSetting',
} as const;
